import { supabase } from '../../providers/supabase/client'

export interface CruiseCabinType {
    id: string;
    name: string;
    category: string;
    icon: string;
    image?: string | null;
    gallery_urls?: string[] | null;
    destinations?: string[] | null;
    ship_ids?: string[] | null;
    capacity?: number | null;
    description?: string | null;
    color: string;
    active: boolean;
    created_at?: string;
    updated_at?: string;
}

export const fetchCruiseCabinTypes = async () => {
    return await supabase
        .from('cruise_cabin_types')
        .select('*')
        .order('name', { ascending: true })
}

export const createCruiseCabinType = async (cabinType: CruiseCabinType) => {
    return await supabase
        .from('cruise_cabin_types')
        .insert([cabinType])
        .select()
}

export const updateCruiseCabinType = async (id: string, cabinType: Partial<CruiseCabinType>) => {
    return await supabase
        .from('cruise_cabin_types')
        .update(cabinType)
        .eq('id', id)
        .select()
}

export const deleteCruiseCabinType = async (id: string) => {
    return await supabase
        .from('cruise_cabin_types')
        .delete()
        .eq('id', id)
}

// ─── STORAGE ────────────────────────────────────────────────

const BUCKET = 'cruise-cabin-images'

/**
 * Upload a cruise cabin image to a deterministic slot.
 * Slot examples: 'main', 'gallery_0', 'gallery_1', etc.
 * Uploads under cabins/${cabinId}/${slot}.
 * Uses upsert so re-uploads overwrite the same object.
 */
export const uploadCruiseCabinImage = async (
    cabinId: string,
    file: File | Blob,
    slot: string,
): Promise<string | null> => {
    let fileExt = 'webp'
    if ((file as any)?.name && typeof (file as any).name === 'string' && (file as any).name.includes('.')) {
        fileExt = (file as any).name.split('.').pop() || 'webp'
    } else if (file.type && file.type.includes('/')) {
        const sub = file.type.split('/')[1]
        fileExt = sub === 'jpeg' ? 'jpg' : (sub || 'webp')
    }

    const contentType = file.type || (fileExt === 'webp' ? 'image/webp' : `image/${fileExt}`)
    const path = `cabins/${cabinId}/${slot}.${fileExt}`

    const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
            upsert: true,
            contentType,
            cacheControl: '0',
        })

    if (error) {
        console.error(`Error uploading cabin image [${slot}]:`, error)
        return null
    }

    const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path)

    return urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null
}

/**
 * Delete a specific cruise cabin image from storage by slot.
 */
export const deleteCruiseCabinImage = async (
    cabinId: string,
    slot: string,
): Promise<boolean> => {
    const extensions = ['webp', 'jpg', 'jpeg', 'png', 'gif']
    const paths = extensions.map(ext => `cabins/${cabinId}/${slot}.${ext}`)

    const { error } = await supabase.storage
        .from(BUCKET)
        .remove(paths)

    if (error) {
        console.error(`Error deleting cabin image [${slot}]:`, error)
        return false
    }
    return true
}
